import type { ReactElement } from 'react';

import { Skeleton } from '@/components/ui/skeleton';

/**
 * Per-route skeleton for the public profile page at `/@:username`
 * (and the `/u/:username` alias). Mirrors the production layout —
 * compact hero with banner + overlapping avatar in the left column,
 * stats rail in the right column, lists grid below the hero on the
 * left. Renders the same `grid-cols-[minmax(0,1fr)_320px]` so the
 * skeleton-to-real transition doesn't reflow the page.
 *
 * No internal `DelayedSkeleton` — `RouteSkeleton` wraps externally.
 */
const STATS_TILE_KEYS = [
  'sk-tile-1',
  'sk-tile-2',
  'sk-tile-3',
] as const;

const STATUS_BAR_KEYS = [
  'sk-bar-1',
  'sk-bar-2',
  'sk-bar-3',
  'sk-bar-4',
  'sk-bar-5',
] as const;

const LIST_CARD_KEYS = [
  'sk-card-1',
  'sk-card-2',
  'sk-card-3',
  'sk-card-4',
  'sk-card-5',
  'sk-card-6',
  'sk-card-7',
  'sk-card-8',
  'sk-card-9',
  'sk-card-10',
] as const;

function HeroSkeleton(): ReactElement {
  return (
    <section className="overflow-hidden rounded-2xl border border-border/60 bg-card/80 shadow-sm">
      {/* Banner — matches the production h-32 sm:h-40 md:h-48 */}
      <Skeleton className="h-32 w-full rounded-none sm:h-40 md:h-48" />

      <div className="px-4 pb-4 pt-3 sm:px-5 sm:pb-5">
        {/* Avatar with overlap */}
        <Skeleton className="-mt-10 mb-2 h-20 w-20 rounded-full sm:-mt-12 sm:h-24 sm:w-24" />

        {/* Display name row + badges placeholder */}
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
          <Skeleton className="h-6 w-32 sm:h-7" />
          <Skeleton className="h-5 w-12 rounded-full" />
          <Skeleton className="h-5 w-14 rounded-full" />
        </div>
        {/* @handle line */}
        <Skeleton className="mt-1 h-3 w-24" />

        {/* Bio */}
        <div className="mt-4 max-w-2xl space-y-2">
          <Skeleton className="h-3 w-full" />
          <Skeleton className="h-3 w-5/6" />
          <Skeleton className="h-3 w-2/3" />
        </div>

        {/* Meta chips */}
        <div className="mt-3 flex flex-wrap gap-1.5">
          <Skeleton className="h-6 w-32 rounded-full" />
          <Skeleton className="h-6 w-40 rounded-full" />
        </div>
      </div>
    </section>
  );
}

function StatsBlockSkeleton({
  accentKey,
}: {
  accentKey: 'A' | 'M';
}): ReactElement {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Skeleton className="h-5 w-5 rounded-md" />
          <Skeleton className="h-3 w-16" />
        </div>
        <Skeleton className="h-3 w-14" />
      </div>
      <div className="grid grid-cols-3 gap-1.5">
        {STATS_TILE_KEYS.map((key) => (
          <div
            key={`${accentKey}-${key}`}
            className="flex flex-col gap-1.5 rounded-xl border border-border/50 bg-background/40 px-2.5 py-2"
          >
            <Skeleton className="h-5 w-10" />
            <Skeleton className="h-2.5 w-12" />
          </div>
        ))}
      </div>
      <div className="space-y-2">
        {STATUS_BAR_KEYS.map((key) => (
          <div key={`${accentKey}-${key}`} className="space-y-1">
            <div className="flex items-center justify-between">
              <Skeleton className="h-2.5 w-16" />
              <Skeleton className="h-2.5 w-10" />
            </div>
            <Skeleton className="h-1.5 w-full rounded-full" />
          </div>
        ))}
      </div>
    </div>
  );
}

function StatsRailSkeleton(): ReactElement {
  return (
    <div className="space-y-3">
      {/* Stats container — anime + manga blocks */}
      <div className="space-y-5 rounded-2xl border border-border/60 bg-card/80 p-4 shadow-sm">
        <Skeleton className="h-3 w-14" />
        <StatsBlockSkeleton accentKey="A" />
        <div className="h-px bg-border/40" />
        <StatsBlockSkeleton accentKey="M" />
      </div>

      {/* AniList aside card */}
      <div className="rounded-2xl border border-border/60 bg-card/80 p-4 shadow-sm">
        <Skeleton className="mb-3 h-6 w-32 rounded-md" />
        <div className="overflow-hidden rounded-lg border border-border/40 bg-background/40">
          <Skeleton className="h-16 w-full rounded-none" />
          <div className="flex items-center gap-3 p-3">
            <Skeleton className="h-10 w-10 rounded-full" />
            <div className="space-y-1.5">
              <Skeleton className="h-3 w-24" />
              <Skeleton className="h-2.5 w-20" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ListsSkeleton(): ReactElement {
  return (
    <div className="space-y-3">
      {/* Tabs */}
      <div className="flex gap-2">
        <Skeleton className="h-9 w-16 rounded-md" />
        <Skeleton className="h-9 w-16 rounded-md" />
      </div>

      {/* Section header */}
      <div className="flex items-center justify-between">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-3 w-14" />
      </div>

      {/* Toolbar */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <Skeleton className="h-9 w-full max-w-xs" />
        <div className="flex gap-2">
          <Skeleton className="h-9 w-32" />
          <Skeleton className="h-9 w-16" />
        </div>
      </div>

      {/* Status pills */}
      <div className="flex flex-wrap gap-1.5">
        <Skeleton className="h-7 w-14 rounded-full" />
        <Skeleton className="h-7 w-20 rounded-full" />
        <Skeleton className="h-7 w-20 rounded-full" />
        <Skeleton className="h-7 w-16 rounded-full" />
        <Skeleton className="h-7 w-16 rounded-full" />
      </div>

      {/* Card grid */}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-2.5 md:grid-cols-4 lg:grid-cols-5">
        {LIST_CARD_KEYS.map((key) => (
          <div key={key} className="space-y-2 p-0.5">
            <Skeleton className="aspect-[2/2.8] w-full rounded-lg" />
            <div className="space-y-1 px-0.5">
              <Skeleton className="h-3.5 w-3/4" />
              <Skeleton className="h-2.5 w-1/2" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ProfileSkeleton(): ReactElement {
  // Uses a plain `<div>` (not `<main>`) so it composes cleanly inside
  // either the route-level layout `<main>` (Suspense fallback) or the
  // page's own `<main>` (in-page loading state). Avoids the nested-
  // landmark HTML hazard.
  return (
    <div className="mx-auto w-full max-w-6xl px-3 py-6 sm:px-6 sm:py-10">
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_320px] lg:gap-6 xl:grid-cols-[minmax(0,1fr)_360px]">
        <div className="space-y-4 sm:space-y-6 lg:col-start-1 lg:row-start-1">
          <HeroSkeleton />
          {/* Mobile stats toggle placeholder */}
          <Skeleton className="h-8 w-28 rounded-full lg:hidden" />
          <ListsSkeleton />
        </div>
        <aside className="hidden lg:col-start-2 lg:row-span-2 lg:row-start-1 lg:block">
          <StatsRailSkeleton />
        </aside>
      </div>
    </div>
  );
}

export default ProfileSkeleton;
